import { Toggle } from "@faire/slate/components/toggle";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";

import { Button } from "../../../../../components/design-system";
import { COPY } from "../../copy";
import { formatOverviewCurrency } from "../../p8OverviewData";
import { useIsCompact } from "../CardDensity";

import { DottedLabel } from "./DottedLabel";
import { HeaderTooltip } from "./HeaderTooltip";

export function formatChartAxisCurrency(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) {
    return `$${Number((value / 1_000_000).toFixed(1))}m`;
  }
  if (abs >= 1000) {
    return `$${Math.round(value / 1000)}k`;
  }
  return formatOverviewCurrency(value);
}

const NICE_STEP_FACTORS = [1, 1.25, 1.5, 2, 2.5, 3, 4, 5, 6, 7.5] as const;

/** Smallest "round" dollar step (100, 125, 150, ... 1k, 1.25k, ...) at or above the minimum. */
export function findNiceStepDollars(minStepDollars: number): number {
  for (let scale = 100; scale <= 1e12; scale *= 10) {
    for (const factor of NICE_STEP_FACTORS) {
      const candidate = scale * factor;
      if (candidate >= minStepDollars) {
        return candidate;
      }
    }
  }
  return 1e12;
}

type ChartHeadlineProps = {
  title: ReactNode;
  /**
   * Shown in a tooltip on hovering the title, which picks up a dotted underline
   * to advertise it. Omit when the title needs no explanation.
   */
  description?: ReactNode;
  /**
   * Always-visible copy under the title. Use this instead of `description` when
   * the explanation should sit on the card rather than behind a hover.
   */
  subtitle?: ReactNode;
  actions?: ReactNode;
};

export function ChartHeadline({
  title,
  description,
  subtitle,
  actions,
}: ChartHeadlineProps) {
  return (
    <div className="flex shrink-0 items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="text-sm leading-5 font-medium text-[#333]">
          {description ? (
            <HeaderTooltip tooltip={description}>
              <DottedLabel>{title}</DottedLabel>
            </HeaderTooltip>
          ) : (
            title
          )}
        </div>
        {subtitle ? (
          <p className="type-fs-paragraph mt-1 text-[#757575]">{subtitle}</p>
        ) : null}
      </div>
      {/* empty:hidden collapses the flex gap when `actions` renders nothing. */}
      {actions ? <div className="shrink-0 empty:hidden">{actions}</div> : null}
    </div>
  );
}

export function ChartChevronLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M10 3L5 8L10 13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ChartChevronRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M6 3L11 8L6 13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ChartMonthAxisTick({
  x,
  y,
  payload,
}: {
  x?: number;
  y?: number;
  payload?: { value: string };
}) {
  if (x == null || y == null || !payload) {
    return null;
  }
  const [monthName = "", year = ""] = payload.value.trim().split(/\s+/);
  return (
    <g transform={`translate(${x},${y + 8})`}>
      <text textAnchor="middle" fill="#757575" fontSize={11}>
        <tspan x={0} dy={0}>
          {monthName}
        </tspan>
        <tspan x={0} dy={13}>
          {year}
        </tspan>
      </text>
    </g>
  );
}

/** Labeled switch for a chart header, e.g. the "Cumulative" view toggle. */
export function ChartHeaderToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    // `flex`, not `inline-flex`: an inline-level box sits in a line box, so it
    // would shift vertically depending on what else is in the header row.
    <label className="flex cursor-pointer items-center gap-2 text-xs leading-4 whitespace-nowrap text-[#757575]">
      {label}
      <Toggle
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        aria-label={label}
      />
    </label>
  );
}

export type ChartMonthNavState = {
  enabled: boolean;
  canPrevious: boolean;
  canNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
};

export type ChartMonthWindowOptions = {
  /** False hands back every point, for ranges that already fit on the axis. */
  pageable: boolean;
  windowSize: number;
  /** Paging jumps back to the newest months whenever this changes. */
  resetKey: string;
};

export type ChartMonthWindow<T> = {
  data: T[];
  nav: ChartMonthNavState;
  /**
   * Goes on a wrapper around the plot. Keep the hover panel outside it — the
   * panel is positioned against the card, not the sliding plot.
   */
  slideRef: RefObject<HTMLDivElement | null>;
};

/**
 * The parts of the plot that carry a month with them. The grid, the y-axis, the
 * x-axis rule, and any reference line are deliberately absent: they describe the
 * value scale rather than the window, so they hold still while the months move
 * past them.
 */
const PLOT_SLIDE_SELECTORS = [
  ".recharts-xAxis .recharts-cartesian-axis-ticks",
  ".recharts-bar",
  ".recharts-line",
] as const;

const MONTH_SLIDE_DURATION_MS = 320;
const MIN_MONTH_SLIDE_PX = 24;
const MAX_MONTH_SLIDE_PX = 96;

/**
 * A chart's own window onto a longer month series, paged by its own arrows.
 *
 * Each chart holds its own offset rather than sharing one: both cards carry a
 * set of buttons, so paging from one card and having the other move too reads
 * as a bug. The plot slides in from the direction of travel so the jump between
 * windows is legible as movement.
 */
export function useChartMonthWindow<T>(
  data: T[],
  { pageable, windowSize, resetKey }: ChartMonthWindowOptions
): ChartMonthWindow<T> {
  const [paging, setPaging] = useState({ resetKey, offset: 0 });
  const slideRef = useRef<HTMLDivElement | null>(null);
  const previousRender = useRef({ resetKey, offset: 0 });

  const maxOffset = pageable ? Math.max(0, data.length - windowSize) : 0;
  const offset = Math.min(
    paging.resetKey === resetKey ? paging.offset : 0,
    maxOffset
  );
  const windowEnd = data.length - offset;
  const windowData = pageable
    ? data.slice(Math.max(0, windowEnd - windowSize), windowEnd)
    : data;

  useEffect(() => {
    const previous = previousRender.current;
    previousRender.current = { resetKey, offset };
    // A reset drops the window back to the newest months rather than stepping
    // to a neighbor, so there is no direction to animate.
    if (previous.resetKey !== resetKey || previous.offset === offset) {
      return;
    }
    const root = slideRef.current;
    if (
      root == null ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }
    // One band, so a one-month step travels exactly one month's width and the
    // plot reads as being dragged rather than redrawn.
    const bandWidth =
      (root.clientWidth - CHART_Y_AXIS_WIDTH) / Math.max(1, windowSize);
    const distance = Math.min(
      Math.max(bandWidth, MIN_MONTH_SLIDE_PX),
      MAX_MONTH_SLIDE_PX
    );
    // Older months sit to the left of the axis, so paging back drags the plot
    // rightwards and paging forward drags it left.
    const from = offset > previous.offset ? -distance : distance;
    // The y-axis gutter is the plot's left edge. Without this the months would
    // slide out over the value labels the paging is supposed to leave alone.
    const clipPath = `view-box inset(0 0 0 ${CHART_Y_AXIS_WIDTH}px)`;

    for (const selector of PLOT_SLIDE_SELECTORS) {
      for (const node of root.querySelectorAll<SVGGElement>(selector)) {
        if (typeof node.animate !== "function") {
          continue;
        }
        node.style.clipPath = clipPath;
        const animation = node.animate(
          [
            { transform: `translateX(${from}px)` },
            { transform: "translateX(0)" },
          ],
          {
            duration: MONTH_SLIDE_DURATION_MS,
            easing: "cubic-bezier(0.22, 1, 0.36, 1)",
          }
        );
        const clearClip = () => {
          node.style.clipPath = "";
        };
        animation.onfinish = clearClip;
        animation.oncancel = clearClip;
      }
    }
  }, [offset, resetKey, windowSize]);

  return {
    data: windowData,
    slideRef,
    nav: {
      enabled: pageable && maxOffset > 0,
      canPrevious: offset < maxOffset,
      canNext: offset > 0,
      onPrevious: () =>
        setPaging({ resetKey, offset: Math.min(offset + 1, maxOffset) }),
      onNext: () => setPaging({ resetKey, offset: Math.max(0, offset - 1) }),
    },
  };
}

/** Renders nothing for charts that show every point they're handed. */
export function ChartNavButtons({ nav }: { nav?: ChartMonthNavState }) {
  if (!nav?.enabled) {
    return null;
  }
  const { canPrevious, canNext, onPrevious, onNext } = nav;

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        className="bg-fs-surface-primary inline-flex h-6 w-6 items-center justify-center rounded-full border border-[#dfe0e1] text-[#333] disabled:cursor-not-allowed disabled:opacity-40"
        aria-label="Previous months"
        disabled={!canPrevious}
        onClick={onPrevious}
      >
        <ChartChevronLeftIcon />
      </button>
      <button
        type="button"
        className="bg-fs-surface-primary inline-flex h-6 w-6 items-center justify-center rounded-full border border-[#dfe0e1] text-[#333] disabled:cursor-not-allowed disabled:opacity-40"
        aria-label="Next months"
        disabled={!canNext}
        onClick={onNext}
      >
        <ChartChevronRightIcon />
      </button>
    </div>
  );
}

const CHART_AXIS_LABEL_STYLE = {
  fontSize: 11,
  fill: "#757575",
} as const;

/** Bottom gutter holds the x-axis title; the y axis is unlabeled, so no left gutter. */
export const CHART_MARGIN_WITH_AXIS_LABELS = {
  top: 8,
  right: 12,
  bottom: 24,
  left: 0,
} as const;

/**
 * Same plot inset as `CHART_MARGIN_WITH_AXIS_LABELS` without the x-axis title
 * gutter. V3 drops "Month" under the ticks — the dates already say that — so
 * this closes the empty band that would otherwise sit between the axis and
 * the legend.
 */
export const CHART_MARGIN_WITHOUT_X_LABEL = {
  top: 8,
  right: 12,
  bottom: 8,
  left: 0,
} as const;

export function useChartPlotMargin() {
  return useIsCompact()
    ? CHART_MARGIN_WITHOUT_X_LABEL
    : CHART_MARGIN_WITH_AXIS_LABELS;
}

export const CHART_Y_AXIS_WIDTH = 42;

/** Vertical gap between the plot (incl. x-axis title) and the legend. */
export const CHART_LEGEND_GAP_CLASS = "gap-0.5";

/**
 * Vertical gap between the card's title and the plot. V3's cards are shorter,
 * so the plot gets a wider berth under the title rather than starting right
 * below it.
 */
export function useChartPlotTopGapClass() {
  return useIsCompact() ? "mt-6" : "mt-4";
}

export function chartXAxisLabel(value: string, offset = 3) {
  return {
    value,
    position: "bottom" as const,
    offset,
    style: CHART_AXIS_LABEL_STYLE,
  };
}

/** V3 omits the x-axis title; the tick dates already name the month. */
export function useChartXAxisLabel(value: string, offset = 3) {
  return useIsCompact() ? undefined : chartXAxisLabel(value, offset);
}

export type ChartLegendItem = {
  key: string;
  label: string;
  color: string;
  /** A node rather than a string so a tooltip can carry a control. */
  tooltip?: ReactNode;
  /**
   * Required when `tooltip` contains something to click — see the prop of the
   * same name on HeaderTooltip. Separate from `tooltip` being a node because
   * plenty of nodes are still just prose.
   */
  interactiveTooltip?: boolean;
  dashed?: boolean;
  ringColor?: string;
  outline?: boolean;
};

export type ChartTooltipRow = {
  key: string;
  label: string;
  value: number;
  /** 0 is a standalone figure; a deeper row is a component of the row above it. */
  depth?: number;
  /** Omitted for totals, which have no series of their own in the chart. */
  swatch?: CSSProperties;
  /** Takes the swatch slot when a single fill can't describe the row, e.g. a total. */
  swatchNode?: ReactNode;
  /** Separates a figure that isn't part of the tree above it, such as spend. */
  dividerAbove?: boolean;
};

const TOOLTIP_DEPTH_INDENT_CLASS = ["", "pl-[18px]", "pl-[36px]"];

/**
 * Tooltip rows indented by containment, so a total and its parts read as a tree
 * instead of a flat list that happens to add up.
 */
export function ChartTooltipRows({
  rows,
  formatValue = formatOverviewCurrency,
}: {
  rows: readonly ChartTooltipRow[];
  formatValue?: (value: number) => string;
}) {
  return (
    <>
      {rows.map((row) => {
        const hasSwatch = row.swatch != null || row.swatchNode != null;
        return (
          <div
            key={row.key}
            className={`flex items-center gap-2 ${
              TOOLTIP_DEPTH_INDENT_CLASS[row.depth ?? 0] ?? ""
            } ${row.dividerAbove ? "mt-1 border-t border-[#eeedea] pt-1" : ""}`}
          >
            {/* The slot is kept even when empty, so a row without a swatch still
                lines its label up with the rows that have one. */}
            {row.swatchNode ?? (
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                style={row.swatch}
              />
            )}
            <span
              className={`whitespace-nowrap ${hasSwatch ? "" : "font-medium"}`}
            >
              {row.label}
            </span>
            <span className="ml-auto pl-6 font-medium whitespace-nowrap">
              {formatValue(row.value)}
            </span>
          </div>
        );
      })}
    </>
  );
}

/**
 * Long enough to cross the gap between the bar and the panel without the panel
 * vanishing, short enough that it doesn't linger once you've moved on.
 */
const HOVER_PANEL_CLOSE_DELAY_MS = 150;

/** Distance from the hovered bar to the panel's near edge. */
const HOVER_PANEL_GAP_PX = 12;

type ChartHoverState = {
  /** The month label of the hovered category; also the panel's own heading. */
  label: string;
  x: number;
  y: number;
  /** Anchor the panel's right edge to x instead of its left, near the edges. */
  flipX: boolean;
  flipY: boolean;
};

/**
 * A hover panel that can hold a control.
 *
 * Recharts' own tooltip can't: it is click-through, and it tracks the cursor, so
 * a button inside it retreats as you reach for it. Pinning it doesn't help
 * either — the pointer crosses other bars on the way, and the panel would
 * re-point at whichever one it landed on.
 *
 * So the panel lives outside the Recharts wrapper, which is what makes it work:
 * the chart never sees the pointer once it's on the panel, so the panel holds
 * still and keeps its month. The chart only reports which bar is active; this
 * hook owns when the panel opens and closes, and freezes the position until the
 * active month actually changes so it doesn't drift under the cursor.
 */
export function useChartHoverPanel() {
  const [hover, setHover] = useState<ChartHoverState | null>(null);
  const boundsRef = useRef<HTMLDivElement | null>(null);
  const closeTimer = useRef<number | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  useEffect(() => cancelClose, [cancelClose]);

  const onChartMouseMove = useCallback(
    (state: {
      activeLabel?: string;
      activeCoordinate?: { x: number; y: number };
    }) => {
      const label = state?.activeLabel;
      const coordinate = state?.activeCoordinate;
      // Recharts types both halves of the coordinate as optional, and a chart
      // reports a mouse move before it has laid out an active band.
      if (
        !label ||
        typeof coordinate?.x !== "number" ||
        typeof coordinate?.y !== "number"
      ) {
        return;
      }
      cancelClose();
      setHover((previous) => {
        if (previous?.label === label) {
          return previous;
        }
        const box = boundsRef.current;
        return {
          label,
          x: coordinate.x,
          y: coordinate.y,
          // Two thirds rather than half: the panel is much wider than it is
          // tall, so it runs out of room on the right well before the middle.
          flipX: box ? coordinate.x > box.clientWidth * 0.66 : false,
          flipY: box ? coordinate.y > box.clientHeight * 0.6 : false,
        };
      });
    },
    [cancelClose]
  );

  const onChartMouseLeave = useCallback(() => {
    cancelClose();
    closeTimer.current = window.setTimeout(
      () => setHover(null),
      HOVER_PANEL_CLOSE_DELAY_MS
    );
  }, [cancelClose]);

  const onPanelEnter = cancelClose;
  const onPanelLeave = useCallback(() => setHover(null), []);

  return {
    hover,
    /** Goes on the element the panel is positioned inside; must be `relative`. */
    boundsRef,
    onChartMouseMove,
    onChartMouseLeave,
    onPanelEnter,
    onPanelLeave,
  };
}

/**
 * Positions a hover panel beside the bar it describes. Rendered as a sibling of
 * the Recharts wrapper rather than inside it — see `useChartHoverPanel`.
 */
export function ChartHoverPanel({
  hover,
  onMouseEnter,
  onMouseLeave,
  children,
}: {
  hover: ChartHoverState;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  children: ReactNode;
}) {
  const translate = `${hover.flipX ? "translateX(-100%)" : ""} ${
    hover.flipY ? "translateY(-100%)" : ""
  }`.trim();

  return (
    <div
      className="pointer-events-auto absolute z-10"
      style={{
        left: hover.flipX
          ? hover.x - HOVER_PANEL_GAP_PX
          : hover.x + HOVER_PANEL_GAP_PX,
        top: hover.y,
        transform: translate || undefined,
        // An absolutely positioned box is offered only the space between `left`
        // and its container's right edge, so on the last few bars it shrinks and
        // wraps every label. The panel is placed by transform, not by that
        // space, so size it to its content and ignore the offer.
        width: "max-content",
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </div>
  );
}

/**
 * The panel's footer action. Sits under the divider that used to separate ad
 * spend from the sales figures — spend is a measured figure like the rest, so
 * the rule now marks where the figures stop and the action starts.
 */
export function ChartTooltipExplore({ onClick }: { onClick: () => void }) {
  return (
    <div className="mt-1 border-t border-[#eeedea] pt-2">
      <Button variant="plain" onClick={onClick}>
        {COPY.overview.charts.explore}
      </Button>
    </div>
  );
}

/** Legend swatch for a series drawn as a line rather than a filled area. */
export function ChartLineLegendSwatch({
  color,
  dashed = false,
  dotted = false,
  marker = true,
}: {
  color: string;
  dashed?: boolean;
  /** Round-capped gaps, matching a dotted reference line. */
  dotted?: boolean;
  /** False for a reference line that has no plot dots of its own. */
  marker?: boolean;
}) {
  return (
    <svg
      width="20"
      height="12"
      viewBox="0 0 20 12"
      aria-hidden
      className="shrink-0"
    >
      <line
        x1="0"
        y1="6"
        x2="20"
        y2="6"
        stroke={color}
        // Dots read thinner than a solid rule at the same width, so the dotted
        // swatch is drawn a pixel heavier to match the line it stands for.
        strokeWidth={dotted ? "2.5" : "1.5"}
        strokeLinecap={dotted ? "round" : undefined}
        strokeDasharray={dotted ? "0 5" : dashed ? "4 3" : undefined}
      />
      {marker ? <circle cx="10" cy="6" r="3" fill={color} /> : null}
    </svg>
  );
}

type ChartLegendProps = {
  items: readonly ChartLegendItem[];
  renderSwatch?: (item: ChartLegendItem) => ReactNode;
};

export function ChartLegend({ items, renderSwatch }: ChartLegendProps) {
  const isCompact = useIsCompact();
  const labelColorClass = isCompact ? "text-[#333]" : "text-[#757575]";
  const underlineClass = isCompact
    ? "border-b border-dotted border-[#333]"
    : "border-b border-dotted border-[#757575]";

  return (
    <div className="flex shrink-0 flex-wrap justify-center gap-4">
      {items.map((item) => {
        const swatch = renderSwatch?.(item) ?? (
          <span
            className="h-3 w-3 rounded-sm"
            style={{
              backgroundColor: item.color,
              border:
                item.dashed || item.outline
                  ? `1px dashed ${item.ringColor ?? item.color}`
                  : undefined,
            }}
          />
        );

        return (
          <span
            key={item.key}
            className={`inline-flex items-center gap-1 text-xs leading-4 whitespace-nowrap ${labelColorClass}`}
          >
            {swatch}
            {item.tooltip ? (
              <HeaderTooltip
                tooltip={item.tooltip}
                interactive={item.interactiveTooltip}
              >
                <span className={underlineClass}>{item.label}</span>
              </HeaderTooltip>
            ) : (
              item.label
            )}
          </span>
        );
      })}
    </div>
  );
}
